import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';
import { useFavorites } from '@/hooks/useFavorites';
import { useHistory } from '@/hooks/useHistory';
import { formatCount } from '@/utils/video';

interface PlayerPanelProps {
  video: VideoItem;
}

export default function PlayerPanel({ video }: PlayerPanelProps) {
  const episodeCount = Math.max(video.episodes, 1);
  const { getEntry, recordWatch } = useHistory();
  const existing = getEntry(video.id);
  const [activeEpisode, setActiveEpisode] = useState(existing?.episode || 1);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const favorited = isFavorite(video.id);

  const handlePlay = (episode: number) => {
    setActiveEpisode(episode);
    recordWatch(video.id, episode, Math.floor(Math.random() * 60) + 10);
  };

  const progressEntry = getEntry(video.id);

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-secondary-950">
        <img
          src={video.cover}
          alt={`${video.title} 播放画面演示`}
          title={`${video.title} - 在线观看`}
          className="w-full h-full object-cover object-top opacity-60"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/40">
          <button
            type="button"
            onClick={() => handlePlay(activeEpisode)}
            className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 transition-colors cursor-pointer"
          >
            <i className="ri-play-fill text-3xl md:text-4xl"></i>
          </button>
          <p className="text-background-50 text-sm">
            {video.episodes > 0 ? `第 ${activeEpisode} 集 · 演示播放` : '演示播放'}
          </p>
          <span className="px-3 py-1 rounded-full bg-background-50/15 text-background-100 text-xs backdrop-blur">
            本播放器为前端演示，不接入真实流媒体服务
          </span>
        </div>
      </div>

      {progressEntry && (
        <p className="mt-3 text-xs text-foreground-500 flex items-center gap-1.5">
          <i className="ri-history-line text-primary-500"></i>
          上次观看至第 {progressEntry.episode} 集，进度 {progressEntry.progress}%（本地记录）
        </p>
      )}

      {video.episodes > 0 && (
        <div className="mt-6">
          <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">剧集选择</h3>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
            {Array.from({ length: episodeCount }, (_, i) => i + 1).map((ep) => (
              <button
                key={ep}
                type="button"
                onClick={() => handlePlay(ep)}
                className={`w-12 h-10 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  ep === activeEpisode
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {ep}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => toggleFavorite(video.id)}
          className={`flex items-center gap-2 h-10 px-5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
            favorited
              ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
              : 'bg-background-100 text-foreground-700 hover:bg-background-200'
          }`}
        >
          <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
          {favorited ? '已收藏' : '收藏'}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShareOpen((v) => !v)}
            className="flex items-center gap-2 h-10 px-5 rounded-md text-sm font-medium bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-forward-line"></i>
            分享
          </button>
          {shareOpen && (
            <div className="absolute left-0 top-12 z-20 w-48 rounded-md bg-background-50 border border-background-200 p-2 animate-fade-in">
              {['复制链接', '分享到微博', '分享到朋友圈'].map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setShareOpen(false)}
                  className="w-full text-left px-3 py-2 rounded text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-eye-line"></i>
          {formatCount(video.views)} 次观看
        </span>
        <span className="flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-thumb-up-line"></i>
          {formatCount(video.likes)} 点赞
        </span>
      </div>

      <p className="mt-4 text-xs text-foreground-400">
        <Link to="/about" className="hover:text-primary-700 cursor-pointer">了解更多关于本站演示性质的说明</Link>
      </p>
    </div>
  );
}